/**
 * 腾讯云COS上传工具类
 */
import COS from 'cos-js-sdk-v5'
import { COS_CONFIG, getPathPrefix, generateUniqueFileName } from './cosConfig'

// 创建COS实例
const cos = new COS({
  SecretId: COS_CONFIG.SecretId,
  SecretKey: COS_CONFIG.SecretKey,
  // 可选：使用临时密钥时的配置
  // getAuthorization: function (options, callback) {
  //   // 异步获取临时密钥
  //   callback({
  //     TmpSecretId: tmpSecretId,
  //     TmpSecretKey: tmpSecretKey,
  //     SecurityToken: sessionToken,
  //     ExpiredTime: expiredTime
  //   })
  // }
})

/**
 * 上传文件到COS
 * @param {Object} options - 上传选项
 * @param {File} options.file - 要上传的文件对象
 * @param {string} options.folder - 文件夹路径（可选）
 * @param {string} options.fileName - 自定义文件名（可选）
 * @param {Function} options.onProgress - 上传进度回调
 * @param {Function} options.onSuccess - 上传成功回调
 * @param {Function} options.onError - 上传失败回调
 * @returns {Promise} 上传任务
 */
export function uploadFile(options) {
  const {
    file,
    folder = '',
    fileName = '',
    onProgress,
    onSuccess,
    onError
  } = options

  return new Promise((resolve, reject) => {
    // 获取文件扩展名
    const fileExt = file.name.substring(file.name.lastIndexOf('.') + 1)
    
    // 根据文件类型获取路径前缀
    const pathPrefix = folder || getPathPrefix(fileExt)
    
    // 生成文件名
    const finalFileName = fileName || generateUniqueFileName(file.name)
    
    // 完整的文件路径
    const key = `${pathPrefix}${finalFileName}`

    // 上传文件
    cos.uploadFile({
      Bucket: COS_CONFIG.Bucket,
      Region: COS_CONFIG.Region,
      Key: key,
      Body: file,
      SliceSize: 1024 * 1024 * 10, // 大于10MB使用分块上传，支持大文件
      onProgress: function (progressData) {
        const percent = Math.round(progressData.percent * 100)
        console.log('上传进度:', percent + '%')
        
        if (onProgress && typeof onProgress === 'function') {
          onProgress({
            percent: percent,
            loaded: progressData.loaded,
            total: progressData.total,
            speed: progressData.speed
          })
        }
      }
    }, function (err, data) {
      if (err) {
        console.error('上传失败:', err)
        
        if (onError && typeof onError === 'function') {
          onError(err)
        }
        
        reject(err)
      } else {
        console.log('上传成功:', data)
        
        const result = {
          url: `https://${data.Location}`,
          key: key,
          fileName: finalFileName,
          fileSize: file.size,
          fileType: file.type,
          ...data
        }
        
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result)
        }
        
        resolve(result)
      }
    })
  })
}

/**
 * 批量上传文件
 * @param {Array} files - 文件数组
 * @param {Object} options - 上传选项
 * @returns {Promise} 上传任务数组
 */
export function uploadMultipleFiles(files, options = {}) {
  const uploadTasks = files.map(file => uploadFile({
    file,
    ...options
  }))
  
  return Promise.all(uploadTasks)
}

/**
 * 删除COS上的文件
 * @param {string} key - 文件的Key
 * @returns {Promise}
 */
export function deleteFile(key) {
  return new Promise((resolve, reject) => {
    cos.deleteObject({
      Bucket: COS_CONFIG.Bucket,
      Region: COS_CONFIG.Region,
      Key: key
    }, function (err, data) {
      if (err) {
        console.error('删除失败:', err)
        reject(err)
      } else {
        console.log('删除成功:', data)
        resolve(data)
      }
    })
  })
}

/**
 * 批量删除文件
 * @param {Array} keys - 文件Key数组
 * @returns {Promise}
 */
export function deleteMultipleFiles(keys) {
  return new Promise((resolve, reject) => {
    cos.deleteMultipleObject({
      Bucket: COS_CONFIG.Bucket,
      Region: COS_CONFIG.Region,
      Objects: keys.map(key => ({ Key: key }))
    }, function (err, data) {
      if (err) {
        console.error('批量删除失败:', err)
        reject(err)
      } else {
        console.log('批量删除成功:', data)
        resolve(data)
      }
    })
  })
}

/**
 * 获取文件列表
 * @param {string} prefix - 文件前缀
 * @param {number} maxKeys - 最大返回数量
 * @returns {Promise}
 */
export function getFileList(prefix = '', maxKeys = 1000) {
  return new Promise((resolve, reject) => {
    cos.getBucket({
      Bucket: COS_CONFIG.Bucket,
      Region: COS_CONFIG.Region,
      Prefix: prefix,
      MaxKeys: maxKeys
    }, function (err, data) {
      if (err) {
        console.error('获取文件列表失败:', err)
        reject(err)
      } else {
        resolve(data)
      }
    })
  })
}

export default {
  uploadFile,
  uploadMultipleFiles,
  deleteFile,
  deleteMultipleFiles,
  getFileList,
  cos // 导出COS实例，以便使用其他API
}

